import { isEmpty } from '@sgme/fp';
import { useData } from 'components/common-form/hooks/useData';
import { useFieldMetadata } from 'components/common-form/hooks/useFieldMetadata';
import { useFieldTranslationKey } from 'components/common-form/hooks/useFieldTranslationKey';
import { BlockOrientationFormGroup } from 'components/shared/FormGroup';
import type { MultipleLabelFieldData, MultipleLabelFieldMetadata } from 'models/forms/fields';
import { FC, useMemo } from 'react';
import { useIntl } from 'react-intl';

import type { ProductNodeBuilderProps } from '../ProductNodeBuilder.model';

export const Badge: FC<{ label: string | undefined; e2e: string }> = ({ label, e2e }) => (
  <span className="badge rounded-pill badge-outline-primary me-2 mb-1" data-e2e={e2e}>
    {label}
  </span>
);

export const MultipleBadgeField: FC<ProductNodeBuilderProps> = ({ node: { id }, workspaceType }) => {
  const { formatMessage } = useIntl();
  const [data] = useData<MultipleLabelFieldData>(id);
  const { labels } = useFieldMetadata<MultipleLabelFieldMetadata>(id)!;
  const translationKey = useFieldTranslationKey(id);
  const uniqueLabels = useMemo(() => {
    if (!Array.isArray(data)) {
      return [];
    }
    const badgeLabels = data.map((code: string) =>
      formatMessage({
        id: `${translationKey}.${code}` as any,
        defaultMessage: `${(labels && labels[code]) || code}`,
      }),
    );
    const uniqueBadgeLabels = Array.from(new Set(badgeLabels));
    return uniqueBadgeLabels;
  }, [data, labels, formatMessage, translationKey]);

  if (isEmpty(data)) {
    return null;
  }
  return (
    <BlockOrientationFormGroup inputId={id} label={`form.${translationKey}` as any} workspaceType={workspaceType}>
      <div className="d-flex flex-wrap pt-1">
        {uniqueLabels.map(label => (
          <Badge label={label} key={label} e2e={`badge-${id}`} />
        ))}
      </div>
    </BlockOrientationFormGroup>
  );
};

MultipleBadgeField.displayName = 'MultipleBadgeField';
